import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

// Import configs and components
import { useConfiguration } from '../../../../context/configurationContext';
import { lazyLoadWithDimensions } from '../../../../util/uiHelpers';
import { FormattedMessage } from '../../../../util/reactIntl';

import { ListingCard, IconSpinner, ErrorMessage, NamedLink } from '../../../../components';

import Field, { hasDataInFields } from '../../Field';
import SectionContainer from '../SectionContainer';

import css from './SectionListings.module.css';

const KEY_ARROW_LEFT = 'ArrowLeft';
const KEY_ARROW_RIGHT = 'ArrowRight';
const MAX_MOBILE_SCREEN_WIDTH = 768;

// Compact layout: used when the section is set to 4 columns in Console (and not inside a modal).
// Design: Orchestrated_Listings_Carousel_Design_2026-10-03.md
const COMPACT_COLUMNS = 4;
// Widest viewport (px) that gets the narrow compact layout; the CSS breakpoint --viewportMedium starts at 768px
const COMPACT_NARROW_MAX_WIDTH = 767;
const CONTENT_MAX_WIDTH_PAGES = 1120;
// ⚠️ Mirrors the compact* classes in ListingCard.module.css:
// 10 (padding-top) + 20 (price) + 4 + 36 (title) + 6 + 22 (location row) = 98
const COMPACT_INFO_HEIGHT = 98;
// ⚠️ On phones the seller name and the location pill sit on two rows: 98 - 22 + 44 = 120
const COMPACT_INFO_HEIGHT_NARROW = 120;
// ⚠️ Mirrors .compactTrack padding-top (12px) + padding-bottom (20px) in SectionListings.module.css
const COMPACT_TRACK_PADDING = 12 + 20;
const COMPACT_IMAGE_SIZES = '(max-width: 767px) 45vw, 240px';

const INITIAL_SCROLL_STATE = { atStart: true, atEnd: false, hasOverflow: false };

// Configuration for supported column layouts
// Only 3 and 4 columns are supported in this component
const COLUMN_CONFIG = {
  3: {
    css: css.threeColumns,
    responsiveImageSizes: '(max-width: 767px) 100vw, (max-width: 1024px) 33vw, 330px',
  },
  4: {
    css: css.fourColumns,
    responsiveImageSizes: '(max-width: 767px) 100vw, (max-width: 1024px) 33vw, 240px',
  },
};

/**
 * Get the CSS class for the specified number of columns
 * @param {number} numColumns - Number of columns (3 or 4)
 * @returns {string} CSS class for the column layout, defaults to 3 columns
 */
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[numColumns];
  return config ? config.css : COLUMN_CONFIG[3].css;
};

const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[numColumns];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[3].responsiveImageSizes;
};

const parseAspectRatio = aspectRatio => {
  const [width, height] = aspectRatio.split('/').map(Number);
  return width / height;
};

const isMobileViewport = (maxWidth = MAX_MOBILE_SCREEN_WIDTH) => {
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  return hasMatchMedia ? window.matchMedia(`(max-width: ${maxWidth}px)`)?.matches : false;
};

/**
 * Calculate the dynamic height for the carousel container based on card dimensions
 *
 * ⚠️ This function contains hardcoded values that refer to the properties defined in ListingCard.module.css
 * If you modify ListingCard's font sizes, padding, margins, or layout you need to also update this function to match.
 * See ListingCard.module.css for the properties this function refers to (search ⚠️ in ListingCard.module.css to find the relevant properties).
 *
 * @param {number} numColumns - Number of columns in the layout
 * @param {Object} config - Configuration object containing layout settings
 * @param {number} carouselWidth - Width of the carousel container
 * @param {boolean} isMobileBreakpoint - Whether the viewport is mobile
 * @returns {number} Calculated height in pixels
 */
const calculateCarouselHeight = (
  numColumns,
  config,
  carouselWidth,
  isMobileBreakpoint = false,
  error,
  noListingsFound
) => {
  const errorMessageHeight = 250;
  const noListingsFoundHeight = 220;

  if (error) {
    return errorMessageHeight;
  }

  if (noListingsFound) {
    return noListingsFoundHeight;
  }

  const thumbnailAspectRatio = config.layout.listingImage.aspectRatio;
  const paddingHorizontal = 2 * 32; // 2x32px
  const titleHeightSingleLine = 16;
  const titleHeightDoubleLine = titleHeightSingleLine * 2;
  const cardInfoPadding = 14 + 2; // padding-top + padding-bottom
  const priceHeight = 16 + 4; // height + margin-bottom
  const authorInfoHeight = 24;
  const contentMaxWidthPages = 1120;
  const containerPaddingTop = 32;
  const containerPaddingBottom = 24;

  const priceHeightMobile = 18 + 4; // 18 + margin bottom
  const authorInfoHeightMobile = 18 + 4 + 4; // 18 + padding top + padding bottom
  const titleHeightSingleLineMobile = 18;
  const cardInfoHeightMobile =
    priceHeightMobile + authorInfoHeightMobile + titleHeightSingleLineMobile + cardInfoPadding;

  const parsedAspectRatio = parseAspectRatio(thumbnailAspectRatio);

  const gutters = isMobileBreakpoint ? 0 : numColumns === 3 ? 64 : 96;

  const mainColumnWidth = Math.min(contentMaxWidthPages, carouselWidth);
  const cardWidth =
    (mainColumnWidth - paddingHorizontal - gutters) / (isMobileBreakpoint ? 1 : numColumns);
  const cardImageHeight = cardWidth / parsedAspectRatio;
  const cardInfoHeight = priceHeight + titleHeightSingleLine + authorInfoHeight + cardInfoPadding;

  const totalCardHeight =
    cardImageHeight + (isMobileBreakpoint ? cardInfoHeightMobile : cardInfoHeight);
  const totalWithPaddings = totalCardHeight + containerPaddingTop + containerPaddingBottom;

  return Math.ceil(totalWithPaddings);
};

/**
 * Height of the carousel container in the compact layout.
 *
 * ⚠️ The card width mirrors --compactCardWidth in SectionListings.module.css, and COMPACT_INFO_HEIGHT
 * mirrors the compact* classes in ListingCard.module.css. Keep all three in step.
 *
 * @param {number|null} carouselWidth - Width of the carousel container
 * @param {boolean} isNarrow - Whether the viewport is below the tablet breakpoint (about 2.3 cards across)
 * @returns {number} Calculated height in pixels
 */
const calculateCompactCarouselHeight = (carouselWidth, isNarrow, error, noListingsFound) => {
  const errorMessageHeight = 250;
  const noListingsFoundHeight = 220;

  if (error) {
    return errorMessageHeight;
  }

  if (noListingsFound) {
    return noListingsFoundHeight;
  }

  const width = carouselWidth || 0;
  const cardWidth = isNarrow
    ? (width - 40) / 2.3
    : (Math.min(CONTENT_MAX_WIDTH_PAGES, width) - 160) / 4;

  // The card image is square, so its height equals the card width
  const infoHeight = isNarrow ? COMPACT_INFO_HEIGHT_NARROW : COMPACT_INFO_HEIGHT;
  return Math.ceil(cardWidth + infoHeight + COMPACT_TRACK_PADDING);
};

/**
 * Component that renders the listing cards in a carousel layout
 * Used with lazy loading wrapper for performance optimization
 * @param {Object} props - Component properties
 * @param {number} props.numColumns - Number of columns to display
 * @param {Array} props.listings - Array of listing data
 * @param {React.RefObject|Function} props.sliderRef - Ref object or callback ref for the slider element
 * @param {boolean} props.darkMode - Whether to apply dark mode styling
 * @param {boolean} props.isCompact - Whether to render the compact 4-up cards
 * @param {Function?} props.onSliderScroll - Called when the slider scrolls (compact layout only)
 * @returns {JSX.Element} Carousel container with listing cards
 */
const ListingCarouselComponent = props => {
  const {
    numColumns,
    listings,
    sliderRef,
    darkMode,
    onFetchFeaturedListings,
    fetched,
    inProgress,
    parentPage,
    sectionId,
    config,
    error,
    allSections,
    isInsideContainer,
    isCompact,
    onSliderScroll,
  } = props;

  const listingImageConfig = config.layout.listingImage;

  useEffect(() => {
    if (!fetched && inProgress !== true && !error) {
      onFetchFeaturedListings(sectionId, parentPage, listingImageConfig, allSections);
    }
  }, []);

  if (inProgress == true) {
    return <IconSpinner className={css.centeredContent} />;
  }

  if (error) {
    return (
      <div className={css.centeredMessageContainer} role="alert">
        <h4 className={css.genericErrorTitle}>
          <FormattedMessage id="SectionListings.genericErrorTitle" />
        </h4>
        <ErrorMessage error={error} />
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className={css.centeredMessageContainer} role="status">
        <p className={css.noListingsFound}>
          <FormattedMessage id="SectionListings.noListingsFoundInfo" />
        </p>
        <NamedLink name="SearchPage" className={css.ctaButton}>
          <FormattedMessage id="SectionListings.noListingsFoundCTA" />
        </NamedLink>
      </div>
    );
  }

  return (
    <ul
      className={isCompact ? css.compactTrack : getColumnCSS(numColumns, false)}
      ref={sliderRef}
      role="list"
      onScroll={onSliderScroll}
    >
      {listings.map(listing => (
        <li key={listing.id.uuid} className={css.listItem}>
          <ListingCard
            className={classNames(isCompact ? css.compactCard : css.card, {
              [css.isInsideContainer]: isInsideContainer,
            })}
            aspectRatioClassName={classNames(css.carouselImageHoverEffect, {
              [css.compactImage]: isCompact,
            })}
            listing={listing}
            darkMode={darkMode}
            renderSizes={isCompact ? COMPACT_IMAGE_SIZES : getResponsiveImageSizes(numColumns)}
            lazyLoadImage={!isInsideContainer}
            compact={isCompact}
          />
        </li>
      ))}
      {isCompact ? (
        <li className={css.listItem}>
          <NamedLink name="SearchPage" className={css.compactEndCard}>
            <span className={css.compactEndArrow} aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M3 9h12M10 4l5 5-5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            See all listings
          </NamedLink>
        </li>
      ) : null}
    </ul>
  );
};

const LazyListingCarouselComponent = lazyLoadWithDimensions(ListingCarouselComponent);

/**
 * Main component for rendering a listings section with carousel functionality
 * Supports 3 or 4 column layouts with horizontal scrolling and responsive behavior.
 * With 4 columns (and outside a modal) it renders the compact layout: header row with the Console
 * title plus arrow buttons, square cards, a "See all listings" end card, and a scroll progress bar.
 * @param {Object} props - Component properties
 * @param {string} props.sectionId - Unique identifier for this section
 * @param {number} props.numColumns - Number of columns (3 or 4, defaults to 3)
 * @param {Object} props.appearance - Styling configuration including text color
 * @param {Object} props.title - Title field data
 * @param {Object} props.description - Description field data
 * @param {Object} props.callToAction - CTA button field data
 * @returns {JSX.Element} Complete listings section with header and carousel
 */
const SectionListings = props => {
  const config = useConfiguration();
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns = 3,
    appearance,
    title,
    description,
    callToAction,
    options,
    allSections,
    isInsideContainer,
  } = props;

  const { featuredListings } = options;
  const {
    onFetchFeaturedListings,
    getListingEntitiesById,
    parentPage,
    featuredListingData,
  } = featuredListings;

  const listingIds = featuredListingData?.[sectionId]?.listingIds;
  const listingEntities = listingIds ? getListingEntitiesById(listingIds) : [];

  const fetched = featuredListingData?.[sectionId]?.fetched || false;
  const inProgress = featuredListingData?.[sectionId]?.inProgress;

  const error = featuredListingData?.[sectionId]?.error;

  const numberOfListings = listingEntities?.length > 0 ? listingIds?.length : 0;

  const isCompact = numColumns === COMPACT_COLUMNS && !isInsideContainer;

  const [carouselWidthConstant, setCarouselWidthConstant] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [scrollState, setScrollState] = useState(INITIAL_SCROLL_STATE);

  const containerRef = React.useRef(null);
  const sliderRef = React.useRef(null);
  const barThumbRef = React.useRef(null);

  // force mobile styles if we render this section within a modal
  const isMobile = mounted && isMobileViewport();
  const isMobileBreakpoint = isMobile || isInsideContainer;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // we use resizeObserver to accomodate for an edge case when this section is rendered within a modal
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(entries => {
        const carouselWidth = entries[0].contentRect.width;
        containerRef.current.style.setProperty('--carouselWidth', `${carouselWidth}px`);
        setCarouselWidthConstant(carouselWidth);
      });
      resizeObserver.observe(containerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  // Compact layout: keep the arrow buttons and the progress bar in step with the track.
  // The bar thumb is moved directly in the DOM so scrolling does not re-render every card;
  // React state only changes when an arrow needs to enable/disable or the bar shows/hides.
  const updateScrollState = useCallback(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const maxScroll = slider.scrollWidth - slider.clientWidth;
    const hasOverflow = maxScroll > 1;
    const thumbWidth = hasOverflow ? (slider.clientWidth / slider.scrollWidth) * 100 : 100;
    const thumbLeft = hasOverflow ? (slider.scrollLeft / maxScroll) * (100 - thumbWidth) : 0;

    const thumb = barThumbRef.current;
    if (thumb) {
      thumb.style.width = `${thumbWidth}%`;
      thumb.style.left = `${thumbLeft}%`;
    }

    const next = {
      atStart: slider.scrollLeft <= 1,
      atEnd: !hasOverflow || slider.scrollLeft >= maxScroll - 1,
      hasOverflow,
    };
    setScrollState(prev =>
      prev.atStart === next.atStart &&
      prev.atEnd === next.atEnd &&
      prev.hasOverflow === next.hasOverflow
        ? prev
        : next
    );
  }, []);

  // The slider is rendered by the lazy-loaded carousel, so it can appear after this component mounts.
  // A callback ref lets us measure it as soon as it exists.
  const setSliderRef = useCallback(
    node => {
      sliderRef.current = node;
      if (node) {
        window.requestAnimationFrame(updateScrollState);
      }
    },
    [updateScrollState]
  );

  useEffect(() => {
    if (isCompact) {
      updateScrollState();
    }
  }, [isCompact, carouselWidthConstant, numberOfListings, updateScrollState]);

  // Compact layout: distance between two neighbouring cards (card width + gap), times the cards per page
  const getCompactSlideWidth = () => {
    const cards = sliderRef.current ? sliderRef.current.querySelectorAll('a') : [];
    if (cards.length === 0) return 0;
    const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : cards[0].clientWidth;
    const cardsPerPage = isMobileViewport(COMPACT_NARROW_MAX_WIDTH) ? 2 : numColumns;
    return cardsPerPage * step;
  };

  const onSlideLeft = e => {
    const slider = sliderRef.current;
    if (!slider) return;
    const sliderChildElement = sliderRef.current.firstElementChild?.querySelector('a');
    const slideWidth = isCompact
      ? getCompactSlideWidth()
      : numColumns * sliderChildElement?.clientWidth;
    slider.scrollLeft = slider.scrollLeft - slideWidth;
    // Fix for Safari
    e.target.focus();
  };

  const onSlideRight = e => {
    const slider = sliderRef.current;
    if (!slider) return;
    const sliderChildElement = sliderRef.current.firstElementChild?.querySelector('a');
    const slideWidth = isCompact
      ? getCompactSlideWidth()
      : numColumns * sliderChildElement?.clientWidth;
    slider.scrollLeft = slider.scrollLeft + slideWidth;
    // Fix for Safari
    e.target.focus();
  };

  const onKeyDown = e => {
    if (e.key === KEY_ARROW_LEFT) {
      // Prevent changing cursor position in input
      e.preventDefault();
      onSlideLeft(e);
    } else if (e.key === KEY_ARROW_RIGHT) {
      // Prevent changing cursor position in input
      e.preventDefault();
      onSlideRight(e);
    }
  };

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const darkMode = appearance?.textColor === 'white';
  const noListingsFound = fetched && listingEntities.length === 0;

  const carouselHeight = isCompact
    ? calculateCompactCarouselHeight(
        carouselWidthConstant,
        mounted && isMobileViewport(COMPACT_NARROW_MAX_WIDTH),
        error,
        noListingsFound
      )
    : calculateCarouselHeight(
        numColumns,
        config,
        carouselWidthConstant,
        isMobileBreakpoint,
        error,
        noListingsFound
      );

  const carouselProps = {
    numColumns,
    listings: listingEntities,
    sliderRef: isCompact ? setSliderRef : sliderRef,
    darkMode,
    onFetchFeaturedListings,
    fetched,
    inProgress,
    parentPage,
    sectionId,
    error,
    config,
    allSections,
    isInsideContainer,
    isCompact,
    onSliderScroll: isCompact ? updateScrollState : undefined,
  };

  // Lazy-loaded carousel component renders when in viewport. We don't use lazy loading if component is rendered within a modal
  const carouselContent = isInsideContainer ? (
    <ListingCarouselComponent {...carouselProps} />
  ) : (
    <LazyListingCarouselComponent {...carouselProps} />
  );

  // The "See all listings" end card is one more item in the row, so 4 listings already overflow 4 columns
  const showCompactArrows = numberOfListings >= numColumns;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
    >
      {isCompact ? (
        <div className={css.compactSection}>
          {/* The Console title, description and call to action are left exactly as they are:
              on the homepage they hold the hero search box and category chips. */}
          {hasHeaderFields ? (
            <header className={defaultClasses.sectionDetails}>
              <Field data={title} className={defaultClasses.title} options={fieldOptions} />
              <Field
                data={description}
                className={defaultClasses.description}
                options={fieldOptions}
              />
              <Field
                data={callToAction}
                className={defaultClasses.ctaButton}
                options={fieldOptions}
              />
            </header>
          ) : null}
          <div className={css.compactHeader}>
            <h2 className={css.compactTitle}>Recent listings</h2>
          </div>
          <div className={css.dynamicContainer} style={{ height: carouselHeight }} ref={containerRef}>
            {showCompactArrows ? (
              <>
                <button
                  type="button"
                  className={classNames(css.compactArrow, css.compactArrowPrev)}
                  onClick={onSlideLeft}
                  onKeyDown={onKeyDown}
                  disabled={scrollState.atStart}
                  aria-label="Previous listings"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className={classNames(css.compactArrow, css.compactArrowNext)}
                  onClick={onSlideRight}
                  onKeyDown={onKeyDown}
                  disabled={scrollState.atEnd}
                  aria-label="Next listings"
                >
                  ›
                </button>
              </>
            ) : null}
            {carouselContent}
          </div>
          <div
            className={classNames(css.compactBar, {
              [css.compactBarHidden]: !scrollState.hasOverflow,
            })}
            aria-hidden="true"
          >
            <div className={css.compactBarThumb} ref={barThumbRef} />
          </div>
        </div>
      ) : (
        <>
          {hasHeaderFields ? (
            <header className={defaultClasses.sectionDetails}>
              <Field data={title} className={defaultClasses.title} options={fieldOptions} />
              <Field
                data={description}
                className={defaultClasses.description}
                options={fieldOptions}
              />
              <Field
                data={callToAction}
                className={defaultClasses.ctaButton}
                options={fieldOptions}
              />
            </header>
          ) : null}

          <div className={css.carouselContainer}>
            <div
              className={classNames(css.carouselArrows, {
                [css.notEnoughListings]: numberOfListings <= numColumns,
              })}
            >
              <button
                className={css.carouselArrowPrev}
                onClick={onSlideLeft}
                onKeyDown={onKeyDown}
              >
                ‹
              </button>
              <button
                className={css.carouselArrowNext}
                onClick={onSlideRight}
                onKeyDown={onKeyDown}
              >
                ›
              </button>
            </div>
            <div
              className={css.dynamicContainer}
              style={{ height: carouselHeight }}
              ref={containerRef}
            >
              {carouselContent}
            </div>
          </div>
        </>
      )}
    </SectionContainer>
  );
};
export default SectionListings;
